// ═══════════════════════════════════════════════════════════════════════
// Dispositifs sociaux — échéancier versionné (toutes les versions restent visibles : le passé n'est jamais réécrit),
// mouvements d'une créance, retenues proposées à la Paie (bloquées tant que le fondement juridique manque et que la
// transmission n'est pas ouverte). Partagés par la fiche d'un prêt et la fiche d'une créance.
// ═══════════════════════════════════════════════════════════════════════
import { useState } from 'react';
import { Badge } from '../../design/composants';
import { toArray, libelle } from './api';
import { Etat } from './commun';
import {
  francs, moisLong, dateLongue, ETATS_ECHEANCIER, ETATS_ECHEANCE, CAUSES_ECHEANCIER, NATURES_MOUVEMENT, STATUTS_MOUVEMENT,
  STATUTS_RETENUE, VERDICTS, MOTIFS_BLOCAGE,
} from './apiSocial';
import { BandeauRetenuesSimulation } from './communSocial';

/** Échéancier versionné : onglet par version (la version en vigueur d'abord sélectionnée). */
export function Echeanciers({ versions }) {
  const v = toArray(versions);
  const enVigueur = v.find((x) => x.statut === 'EN_VIGUEUR') || v[v.length - 1];
  const [choix, setChoix] = useState(enVigueur ? enVigueur.id : null);
  if (!v.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun échéancier : il est établi au décaissement.</p>;
  const courante = v.find((x) => x.id === choix) || enVigueur;
  const echeances = toArray(courante.echeances);
  return (
    <div>
      <div className="rh-vues rh-vues-ligne" role="group" aria-label="Versions de l'échéancier" style={{ marginBottom: 10 }}>
        {v.map((x) => (
          <button key={x.id} type="button" className="rh-vue" aria-pressed={x.id === courante.id} onClick={() => setChoix(x.id)}>
            Version {x.version}{x.statut === 'EN_VIGUEUR' ? ' (en vigueur)' : ''}
          </button>
        ))}
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--sg-texte-2)' }}>
        <Etat table={ETATS_ECHEANCIER} code={courante.statut} />{' '}
        {libelle(CAUSES_ECHEANCIER, courante.cause)} · {francs(courante.montant_total)} en {courante.nb_echeances} échéance{courante.nb_echeances > 1 ? 's' : ''}
        {courante.premiere_periode ? ` à partir de ${moisLong(courante.premiere_periode)}` : ''}
        {courante.cree_le ? ` · établi le ${dateLongue(courante.cree_le)}` : ''}
        {courante.simulation ? ' · simulation' : ''}
        {courante.motif ? ` · motif : ${courante.motif}` : ''}
      </p>
      <div className="rh-conteneur-tableau">
        <table className="rh-tableau">
          <thead><tr><th>Échéance</th><th>Mois</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th></tr></thead>
          <tbody>
            {echeances.map((e) => (
              <tr key={e.id || e.rang}>
                <td>{e.libelle || `Paiement ${e.rang} sur ${e.total}`}</td>
                <td>{moisLong(e.periode)}</td>
                <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{francs(e.montant)}</td>
                <td><Etat table={ETATS_ECHEANCE} code={e.statut} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Mouvements d'une créance (ajout seul) ; `actions(m)` rend les boutons d'une ligne. */
export function Mouvements({ mouvements, actions }) {
  const l = toArray(mouvements);
  if (!l.length) return <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucun mouvement.</p>;
  return (
    <div className="rh-conteneur-tableau">
      <table className="rh-tableau">
        <thead><tr><th>Date</th><th>Mouvement</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th>Motif</th>{actions && <th aria-label="Actions" />}</tr></thead>
        <tbody>
          {l.map((m) => (
            <tr key={m.id}>
              <td style={{ whiteSpace: 'nowrap' }}>{dateLongue(m.date_operation)}</td>
              <td>{libelle(NATURES_MOUVEMENT, m.nature)}<span className="rh-cellule-secondaire" style={{ display: 'block' }}>{m.origine === 'MANUELLE' ? 'Saisie manuelle' : 'Automatique'}</span></td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{Number(m.sens) === -1 ? '− ' : '+ '}{francs(m.montant)}</td>
              <td><Etat table={STATUTS_MOUVEMENT} code={m.statut} /></td>
              <td>{m.motif || ''}</td>
              {actions && <td style={{ textAlign: 'right' }}>{actions(m)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Retenues proposées à la Paie pour une créance : état, verdict de l'arbitre et motif de blocage expliqué. */
export function RetenuesProposees({ retenues }) {
  const l = toArray(retenues);
  const bloquees = l.filter((r) => r.verdict === 'BLOQUE');
  return (
    <div>
      <BandeauRetenuesSimulation />
      {!l.length && <p className="rh-cellule-secondaire" style={{ margin: 0 }}>Aucune retenue proposée (remboursement direct, ou décaissement non encore fait).</p>}
      {bloquees.length > 0 && (
        <p style={{ margin: '0 0 10px', fontSize: 14 }}>
          {bloquees.length} retenue{bloquees.length > 1 ? 's sont bloquées' : ' est bloquée'} : elles restent à l'état « proposée » tant que le fondement juridique
          (cession au greffe ou accord validé) n'est pas enregistré et que la transmission à la Paie n'est pas ouverte.
        </p>
      )}
      {l.length > 0 && (
        <div className="rh-conteneur-tableau">
          <table className="rh-tableau">
            <thead><tr><th>Mois</th><th>Échéance</th><th style={{ textAlign: 'right' }}>Montant</th><th>État</th><th>Motif du blocage</th></tr></thead>
            <tbody>
              {l.map((r) => (
                <tr key={r.evenement_id || r.cle_idempotence}>
                  <td>{moisLong(r.periode)}</td>
                  <td>{r.rang ? `${r.rang} sur ${r.total}` : ''}{r.version_echeancier ? <span className="rh-cellule-secondaire" style={{ display: 'block' }}>version {r.version_echeancier}</span> : null}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{francs(r.montant_demande)}</td>
                  <td><Etat table={STATUTS_RETENUE} code={r.statut} />{r.verdict && <div style={{ marginTop: 4 }}><Etat table={VERDICTS} code={r.verdict} /></div>}</td>
                  <td>
                    {toArray(r.motifs_blocage).length ? (
                      <ul style={{ margin: 0, paddingLeft: 16 }}>
                        {toArray(r.motifs_blocage).map((m) => <li key={m} title={m}>{MOTIFS_BLOCAGE[m] || m}</li>)}
                      </ul>
                    ) : <span className="rh-cellule-secondaire">Aucun</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Badge de simulation d'une créance. */
export const BadgeSimulation = ({ actif }) => (actif ? <Badge ton="info" title="Créance de simulation : jamais transmise à la Paie">Simulation</Badge> : null);
